// ============|| ASSISTANT PANEL — SKIN ||============ //
// שפה חזותית לפאנל העוזר: "זכוכית + אורורה" מעל פלטת המערכת.
// כל הצבעים נגזרים מ-theme.palette (primary / secondary) — בלי hex קשיח,
// כך שהפאנל מתיישר אוטומטית לכל preset ולמצב כהה. תנועה: transform/opacity בלבד.
import { alpha, keyframes, type Theme } from '@mui/material/styles';
import type { SystemStyleObject } from '@mui/system';
import { scrollbarRegionSx } from 'themes/scrollbarRegionSx';

const MONO_FONT = 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace';
const EASE = 'cubic-bezier(0.4, 0, 0.2, 1)';
const REDUCED_MOTION = '@media (prefers-reduced-motion: reduce)';

// ============|| KEYFRAMES ||============ //

/** טבעת קונית שמסתובבת סביב האווטאר — רק בזמן עבודה אמיתית */
const orbitSpin = keyframes`
  to { transform: rotate(360deg); }
`;

/** הילה שמתפשטת ודועכת — פעימת נוכחות איטית */
const haloPulse = keyframes`
  0% { opacity: 0.5; transform: scale(0.9); }
  70% { opacity: 0; transform: scale(1.35); }
  100% { opacity: 0; transform: scale(1.35); }
`;

/** נקודת חיים בשורת הסטטוס */
const livePulse = keyframes`
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.45; transform: scale(0.72); }
`;

/** כניסת שורה — הרמה קטנה + opacity */
const riseIn = keyframes`
  from { opacity: 0; transform: translate3d(0, 6px, 0); }
  to { opacity: 1; transform: translate3d(0, 0, 0); }
`;

/** שלוש נקודות "חושב" */
const thinkingDot = keyframes`
  0%, 60%, 100% { opacity: 0.25; transform: translate3d(0, 0, 0); }
  30% { opacity: 1; transform: translate3d(0, -3px, 0); }
`;

// ============|| BUILDING BLOCKS ||============ //

function tones(theme: Theme) {
  return {
    isDark: theme.palette.mode === 'dark',
    primary: theme.palette.primary.main,
    primaryDark: theme.palette.primary.dark,
    secondary: theme.palette.secondary.main,
    paper: theme.palette.background.paper,
    white: theme.palette.common.white,
  };
}

/** אנימציה שנכבית עבור מי שביקש פחות תנועה */
function motion(value: string): SystemStyleObject<Theme> {
  return { animation: value, [REDUCED_MOTION]: { animation: 'none' } };
}

/** קו שיער מתמסמס — מפריד בין אזורים בלי לחתוך אותם */
function hairline(theme: Theme, position: 'top' | 'bottom'): SystemStyleObject<Theme> {
  const { primary, secondary } = tones(theme);
  return {
    content: '""',
    position: 'absolute',
    insetInline: 0,
    [position]: 0,
    height: '1px',
    background: `linear-gradient(90deg, transparent 0%, ${alpha(primary, 0.45)} 24%, ${alpha(secondary, 0.4)} 70%, transparent 100%)`,
    pointerEvents: 'none',
  };
}

// ============|| SHELL ||============ //

/** משטח הפאנל — הקצה הפנימי (inline-start) מעוגל ומואר, שאר הקצוות צמודים */
export function assistantPaperSx(theme: Theme): SystemStyleObject<Theme> {
  const { isDark, primary, secondary, paper } = tones(theme);
  return {
    width: { xs: '100%', sm: 420 },
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    overflow: 'hidden',
    border: 0,
    borderInlineStart: `1px solid ${alpha(primary, isDark ? 0.28 : 0.16)}`,
    borderStartStartRadius: { xs: 0, sm: '18px' },
    borderEndStartRadius: { xs: 0, sm: '18px' },
    backgroundColor: paper,
    backgroundImage: [
      `radial-gradient(120% 52% at 100% 0%, ${alpha(primary, isDark ? 0.22 : 0.12)} 0%, transparent 62%)`,
      `radial-gradient(95% 45% at 0% 6%, ${alpha(secondary, isDark ? 0.16 : 0.09)} 0%, transparent 58%)`,
      `radial-gradient(130% 55% at 45% 100%, ${alpha(secondary, isDark ? 0.13 : 0.07)} 0%, transparent 62%)`,
    ].join(', '),
    boxShadow: isDark
      ? `0 0 60px -18px ${alpha(primary, 0.4)}`
      : `0 0 50px -18px ${alpha(primary, 0.24)}`,
  };
}

/** רקע ה-Modal — הטשטוש מרחיק את המסך שמאחור בלי להחשיך אותו */
export function assistantBackdropSx(theme: Theme): SystemStyleObject<Theme> {
  return {
    backgroundColor: alpha(theme.palette.common.black, theme.palette.mode === 'dark' ? 0.55 : 0.3),
    backdropFilter: 'blur(3px)',
    WebkitBackdropFilter: 'blur(3px)',
  };
}

// ============|| HEADER ||============ //

export function assistantHeaderSx(theme: Theme): SystemStyleObject<Theme> {
  const { isDark, primary, paper } = tones(theme);
  return {
    position: 'relative',
    zIndex: 2,
    flexShrink: 0,
    px: 2,
    py: 1.5,
    minHeight: 72,
    background: `linear-gradient(180deg, ${alpha(primary, isDark ? 0.2 : 0.11)} 0%, ${alpha(paper, isDark ? 0.42 : 0.66)} 100%)`,
    backdropFilter: 'blur(14px) saturate(1.2)',
    WebkitBackdropFilter: 'blur(14px) saturate(1.2)',
    '&::after': hairline(theme, 'bottom'),
  };
}

/** אווטאר המותג — squircle עם גרדיאנט, וטבעת מסתובבת כשהעוזר עובד */
export function assistantBrandAvatarSx(theme: Theme, active: boolean): SystemStyleObject<Theme> {
  const { primary, primaryDark, secondary, white } = tones(theme);
  return {
    position: 'relative',
    flexShrink: 0,
    width: '42px',
    height: '42px',
    borderRadius: '14px',
    display: 'grid',
    placeItems: 'center',
    color: theme.palette.primary.contrastText,
    background: `linear-gradient(135deg, ${primaryDark} 0%, ${primary} 52%, ${secondary} 100%)`,
    boxShadow: `0 8px 20px -8px ${alpha(primary, 0.7)}, inset 0 1px 0 ${alpha(white, 0.32)}`,
    '&::before': {
      content: '""',
      position: 'absolute',
      inset: '-3px',
      borderRadius: '17px',
      padding: '2px',
      background: `conic-gradient(from 0deg, transparent 0deg, ${alpha(secondary, 0.95)} 80deg, ${alpha(primary, 0.95)} 190deg, transparent 300deg)`,
      WebkitMask: 'linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)',
      WebkitMaskComposite: 'xor',
      mask: 'linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)',
      maskComposite: 'exclude',
      opacity: active ? 1 : 0,
      pointerEvents: 'none',
      transition: theme.transitions.create('opacity', { duration: theme.transitions.duration.short }),
      ...(active ? motion(`${orbitSpin} 1.7s linear infinite`) : { animation: 'none' }),
    },
  };
}

/** תג "MCP" — חתימה טכנית זעירה לצד השם */
export function assistantProtocolBadgeSx(theme: Theme): SystemStyleObject<Theme> {
  const { isDark, secondary } = tones(theme);
  return {
    direction: 'ltr',
    px: 0.625,
    py: '1px',
    borderRadius: '6px',
    fontFamily: MONO_FONT,
    fontSize: '0.5625rem',
    fontWeight: 700,
    letterSpacing: '0.1em',
    lineHeight: 1.7,
    color: isDark ? theme.palette.secondary.light : theme.palette.secondary.dark,
    bgcolor: alpha(secondary, isDark ? 0.18 : 0.09),
    border: `1px solid ${alpha(secondary, isDark ? 0.42 : 0.26)}`,
  };
}

/** נקודת חיים — ירוקה במנוחה, כתומה ופועמת בזמן עבודה */
export function assistantStatusDotSx(theme: Theme, active: boolean): SystemStyleObject<Theme> {
  const color = active ? theme.palette.warning.main : theme.palette.success.main;
  return {
    width: '7px',
    height: '7px',
    borderRadius: '50%',
    flexShrink: 0,
    bgcolor: color,
    boxShadow: `0 0 0 3px ${alpha(color, 0.16)}`,
    ...(active ? motion(`${livePulse} 1.3s ease-in-out infinite`) : {}),
  };
}

/** תג אמון (הרשאות / אישור שליחה) — מבטיח שהחוזה הבטיחותי נראה, לא נקרא כאזהרה */
export function assistantTrustBadgeSx(theme: Theme): SystemStyleObject<Theme> {
  const { isDark } = tones(theme);
  return {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 0.375,
    px: 0.625,
    py: '2px',
    borderRadius: '6px',
    color: 'text.secondary',
    bgcolor: alpha(theme.palette.text.primary, isDark ? 0.07 : 0.035),
    border: `1px solid ${alpha(theme.palette.divider, isDark ? 0.6 : 1)}`,
    fontSize: '0.625rem',
    fontWeight: 500,
    lineHeight: 1.6,
    whiteSpace: 'nowrap',
  };
}

export function assistantHeaderIconButtonSx(theme: Theme): SystemStyleObject<Theme> {
  const { isDark, primary, paper } = tones(theme);
  return {
    width: '32px',
    height: '32px',
    borderRadius: '10px',
    color: 'text.secondary',
    bgcolor: alpha(paper, isDark ? 0.14 : 0.68),
    border: `1px solid ${alpha(primary, isDark ? 0.24 : 0.14)}`,
    transition: theme.transitions.create(['transform', 'box-shadow', 'background-color', 'border-color', 'color'], {
      duration: theme.transitions.duration.shorter,
      easing: theme.transitions.easing.easeInOut,
    }),
    '&:hover': {
      color: 'text.primary',
      bgcolor: alpha(primary, isDark ? 0.2 : 0.09),
      borderColor: alpha(primary, isDark ? 0.44 : 0.3),
      transform: 'translate3d(0, -1px, 0)',
      boxShadow: `0 4px 12px -4px ${alpha(primary, 0.4)}`,
    },
  };
}

/** פס התקדמות דק בתחתית הכותרת — הסימן היחיד שמשהו רץ ברקע */
export function assistantActivityBarSx(theme: Theme): SystemStyleObject<Theme> {
  const { primary, secondary } = tones(theme);
  return {
    position: 'absolute',
    insetInline: 0,
    bottom: 0,
    zIndex: 3,
    height: '2px',
    bgcolor: 'transparent',
    '& .MuiLinearProgress-bar': {
      background: `linear-gradient(90deg, ${alpha(primary, 0.2)} 0%, ${primary} 45%, ${secondary} 100%)`,
    },
  };
}

// ============|| MESSAGES ||============ //

export function assistantMessagesAreaSx(): SystemStyleObject<Theme> {
  return {
    position: 'relative',
    zIndex: 1,
    flex: 1,
    overflowY: 'auto',
    px: 1.75,
    py: 2,
    ...scrollbarRegionSx,
  };
}

/** כניסת פריט לשיחה — הופעה עדינה, בלי דחיפת פריסה */
export function assistantItemEnterSx(): SystemStyleObject<Theme> {
  return motion(`${riseIn} 0.34s ${EASE} both`);
}

/** בועת העוזר — כרטיס זכוכית עם קו שיער בגוון primary */
export function assistantBotBubbleSx(theme: Theme, isError = false): SystemStyleObject<Theme> {
  const { isDark, primary, paper } = tones(theme);
  return {
    position: 'relative',
    minWidth: 0,
    px: 1.5,
    py: 1.125,
    borderRadius: '16px',
    borderStartEndRadius: '5px',
    // אטום־כמעט במקום backdrop-filter: הבועות מתרבות, וטשטוש לכל אחת פוגע בגלילה
    bgcolor: alpha(paper, isDark ? 0.86 : 0.97),
    border: `1px solid ${isError ? theme.palette.error.main : alpha(primary, isDark ? 0.24 : 0.13)}`,
    boxShadow: isError
      ? `0 8px 22px -14px ${alpha(theme.palette.error.main, 0.6)}`
      : `0 1px 2px ${alpha(theme.palette.common.black, isDark ? 0.24 : 0.04)}, 0 10px 24px -16px ${alpha(primary, isDark ? 0.7 : 0.4)}`,
  };
}

/** בועת המשתמש — גרדיאנט מלא, הצד ה"חם" של השיחה */
export function assistantUserBubbleSx(theme: Theme): SystemStyleObject<Theme> {
  const { primary, primaryDark, white } = tones(theme);
  return {
    minWidth: 0,
    px: 1.5,
    py: 1.125,
    borderRadius: '16px',
    borderStartStartRadius: '5px',
    color: white,
    // body2 ב-theme מגדיר text.primary — חייבים לדרוס כדי שיהיה קריא על הגרדיאנט
    '& .MuiTypography-root': { color: 'inherit' },
    background: `linear-gradient(135deg, ${primaryDark} 0%, ${primary} 100%)`,
    boxShadow: `0 10px 22px -12px ${alpha(primary, 0.75)}, inset 0 1px 0 ${alpha(white, 0.18)}`,
  };
}

/** אווטאר קטן לצד בועה */
export function assistantMessageAvatarSx(theme: Theme, variant: 'bot' | 'user'): SystemStyleObject<Theme> {
  const { primary, primaryDark, secondary, isDark, paper } = tones(theme);
  const base: SystemStyleObject<Theme> = {
    width: '30px',
    height: '30px',
    borderRadius: '10px',
    flexShrink: 0,
    mt: 0.25,
    fontSize: '0.75rem',
  };
  if (variant === 'user') {
    return {
      ...base,
      bgcolor: alpha(paper, isDark ? 0.5 : 1),
      color: 'text.secondary',
      border: `1px solid ${alpha(theme.palette.divider, isDark ? 0.6 : 1)}`,
    };
  }
  return {
    ...base,
    display: 'grid',
    placeItems: 'center',
    color: theme.palette.primary.contrastText,
    background: `linear-gradient(135deg, ${primaryDark} 0%, ${primary} 55%, ${secondary} 100%)`,
    boxShadow: `0 6px 14px -8px ${alpha(primary, 0.8)}`,
  };
}

/** שלוש נקודות "חושב" */
export function assistantThinkingDotSx(theme: Theme, index: number): SystemStyleObject<Theme> {
  return {
    width: '5px',
    height: '5px',
    borderRadius: '50%',
    bgcolor: theme.palette.primary.main,
    ...motion(`${thinkingDot} 1.15s ${EASE} ${index * 0.14}s infinite`),
    [REDUCED_MOTION]: { animation: 'none', opacity: 0.5 },
  };
}

/** עקבת כלי — נראית כמו שורת לוג, לא כמו הודעה */
export function assistantToolChipSx(theme: Theme, running: boolean): SystemStyleObject<Theme> {
  const { isDark, primary } = tones(theme);
  return {
    alignSelf: 'flex-end',
    direction: 'ltr',
    height: '24px',
    borderRadius: '8px',
    fontFamily: MONO_FONT,
    fontSize: '0.6875rem',
    color: 'text.secondary',
    bgcolor: alpha(primary, isDark ? 0.1 : 0.05),
    border: `1px ${running ? 'dashed' : 'solid'} ${alpha(primary, isDark ? 0.34 : 0.2)}`,
    ...(running && { boxShadow: `0 0 0 4px ${alpha(primary, isDark ? 0.09 : 0.05)}` }),
  };
}

// ============|| EMPTY STATE ||============ //

/** גליף הפתיחה — squircle עם גרדיאנט ושתי טבעות שנושמות */
export function assistantEmptyGlyphSx(theme: Theme): SystemStyleObject<Theme> {
  const { primary, primaryDark, secondary, white } = tones(theme);
  const ring = (delay: string): SystemStyleObject<Theme> => ({
    content: '""',
    position: 'absolute',
    inset: '-9px',
    borderRadius: '29px',
    border: `1px solid ${alpha(primary, 0.3)}`,
    pointerEvents: 'none',
    ...motion(`${haloPulse} 3.8s ${EASE} ${delay} infinite`),
  });
  return {
    position: 'relative',
    width: '62px',
    height: '62px',
    borderRadius: '20px',
    display: 'grid',
    placeItems: 'center',
    color: theme.palette.primary.contrastText,
    background: `linear-gradient(135deg, ${primaryDark} 0%, ${primary} 50%, ${secondary} 100%)`,
    boxShadow: `0 16px 34px -16px ${alpha(primary, 0.85)}, inset 0 1px 0 ${alpha(white, 0.3)}`,
    '&::before': ring('0s'),
    '&::after': ring('1.9s'),
  };
}

/** צ'יפ הצעה — שורה מלאה, קריאה לפעולה שקטה */
export function assistantSuggestionSx(theme: Theme, index: number): SystemStyleObject<Theme> {
  const { isDark, primary, paper } = tones(theme);
  return {
    width: '100%',
    height: 'auto',
    minHeight: 38,
    justifyContent: 'flex-start',
    borderRadius: '12px',
    fontSize: '0.8125rem',
    fontWeight: 500,
    py: 0.625,
    color: 'text.primary',
    bgcolor: alpha(paper, isDark ? 0.28 : 0.82),
    border: `1px solid ${alpha(primary, isDark ? 0.26 : 0.15)}`,
    ...motion(`${riseIn} 0.4s ${EASE} ${80 + index * 70}ms both`),
    '& .MuiChip-label': { flex: 1, textAlign: 'start', whiteSpace: 'normal' },
    transition: theme.transitions.create(['transform', 'box-shadow', 'background-color', 'border-color'], {
      duration: theme.transitions.duration.shorter,
      easing: theme.transitions.easing.easeInOut,
    }),
    '&:hover': {
      bgcolor: alpha(primary, isDark ? 0.16 : 0.07),
      borderColor: alpha(primary, isDark ? 0.5 : 0.34),
      transform: 'translate3d(0, -1px, 0)',
      boxShadow: `0 6px 16px -8px ${alpha(primary, 0.5)}`,
    },
  };
}

// ============|| COMPOSER ||============ //

export function assistantComposerSx(theme: Theme): SystemStyleObject<Theme> {
  const { isDark, primary, paper } = tones(theme);
  return {
    position: 'relative',
    zIndex: 2,
    flexShrink: 0,
    px: 1.75,
    pt: 1.375,
    pb: 1.625,
    background: `linear-gradient(180deg, ${alpha(paper, isDark ? 0.3 : 0.62)} 0%, ${alpha(primary, isDark ? 0.14 : 0.07)} 100%)`,
    backdropFilter: 'blur(14px) saturate(1.15)',
    WebkitBackdropFilter: 'blur(14px) saturate(1.15)',
    '&::before': hairline(theme, 'top'),
  };
}

export function assistantInputSx(theme: Theme): SystemStyleObject<Theme> {
  const { isDark, primary, paper } = tones(theme);
  return {
    flex: 1,
    minWidth: 0,
    '& .MuiOutlinedInput-root': {
      borderRadius: '22px',
      bgcolor: alpha(paper, isDark ? 0.4 : 0.9),
      fontSize: '0.875rem',
      transition: theme.transitions.create(['box-shadow', 'background-color'], {
        duration: theme.transitions.duration.shorter,
      }),
      '& fieldset': { borderColor: alpha(primary, isDark ? 0.28 : 0.18) },
      '&:hover fieldset': { borderColor: alpha(primary, isDark ? 0.44 : 0.32) },
      '&.Mui-focused': {
        bgcolor: paper,
        boxShadow: `0 0 0 3px ${alpha(primary, isDark ? 0.24 : 0.16)}`,
      },
    },
  };
}

export function assistantSendButtonSx(theme: Theme, ready: boolean): SystemStyleObject<Theme> {
  const { isDark, primary, primaryDark, secondary, white } = tones(theme);
  return {
    width: '40px',
    height: '40px',
    flexShrink: 0,
    borderRadius: '14px',
    color: ready ? theme.palette.primary.contrastText : 'text.disabled',
    background: ready
      ? `linear-gradient(135deg, ${primaryDark} 0%, ${primary} 55%, ${secondary} 100%)`
      : alpha(theme.palette.text.primary, isDark ? 0.1 : 0.06),
    boxShadow: ready ? `0 8px 18px -8px ${alpha(primary, 0.75)}, inset 0 1px 0 ${alpha(white, 0.24)}` : 'none',
    transition: theme.transitions.create(['transform', 'box-shadow', 'background-color', 'color'], {
      duration: theme.transitions.duration.shorter,
      easing: theme.transitions.easing.easeInOut,
    }),
    '&.Mui-disabled': {
      color: 'text.disabled',
      background: alpha(theme.palette.text.primary, isDark ? 0.1 : 0.06),
    },
    '&:hover': {
      background: `linear-gradient(135deg, ${primaryDark} 0%, ${primary} 45%, ${secondary} 100%)`,
      transform: 'translate3d(0, -1px, 0) scale(1.04)',
      boxShadow: `0 10px 22px -8px ${alpha(primary, 0.85)}`,
    },
  };
}

// ============|| FLOATING ENTRY POINT ||============ //

/**
 * הכפתור הצף תופס פינה קבועה בתחתית ה-inline-start (52px גובה מעל offset של 16/24).
 * פוטרים שכפתורי הפעולה שלהם יורדים לאותה פינה — למשל "הבא" באשפי התפוצה, שב-RTL
 * מתרנדר בשמאל הפיזי — חייבים לפנות את הגובה הזה כדי שלא ייחבאו מתחתיו.
 */
export const ASSISTANT_FAB_CLEARANCE_SPACING = { xs: 7.5, sm: 9 } as const;

/** מעטפת הכפתור הצף — מחזיקה את ההילה כדי שלא תיחתך ע"י overflow של Fab */
export function assistantFabDockSx(theme: Theme): SystemStyleObject<Theme> {
  const { primary } = tones(theme);
  return {
    position: 'fixed',
    bottom: { xs: 16, sm: 24 },
    // הצינור RTL של האפליקציה הופך left/right — `right` כאן מתרנדר בשמאל הפיזי.
    right: { xs: 16, sm: 24 },
    zIndex: theme.zIndex.snackbar,
    display: 'flex',
    width: '52px',
    height: '52px',
    '&::after': {
      content: '""',
      position: 'absolute',
      inset: 0,
      borderRadius: '18px',
      border: `1px solid ${alpha(primary, 0.45)}`,
      pointerEvents: 'none',
      ...motion(`${haloPulse} 3.6s ${EASE} infinite`),
    },
  };
}

export function assistantFabSx(theme: Theme): SystemStyleObject<Theme> {
  const { primary, primaryDark, secondary, white } = tones(theme);
  return {
    width: '52px',
    height: '52px',
    minHeight: 0,
    borderRadius: '18px',
    color: theme.palette.primary.contrastText,
    background: `linear-gradient(135deg, ${primaryDark} 0%, ${primary} 52%, ${secondary} 100%)`,
    boxShadow: `0 12px 26px -10px ${alpha(primary, 0.8)}, 0 2px 6px ${alpha(theme.palette.common.black, 0.16)}, inset 0 1px 0 ${alpha(white, 0.28)}`,
    transition: theme.transitions.create(['transform', 'box-shadow'], {
      duration: theme.transitions.duration.short,
      easing: theme.transitions.easing.easeInOut,
    }),
    '&:hover': {
      background: `linear-gradient(135deg, ${primaryDark} 0%, ${primary} 44%, ${secondary} 100%)`,
      transform: 'translate3d(0, -2px, 0) scale(1.04)',
      boxShadow: `0 16px 32px -10px ${alpha(primary, 0.9)}, inset 0 1px 0 ${alpha(white, 0.32)}`,
    },
    '&:active': { transform: 'translate3d(0, 0, 0) scale(0.98)' },
  };
}

// ============|| CONFIRMATION CARD ||============ //

/** כרטיס אישור — נבדל מהשיחה ע"י פס warning בקצה ההתחלה, בלי צעקה */
export function assistantConfirmationCardSx(theme: Theme, resolved: boolean): SystemStyleObject<Theme> {
  const { isDark, paper } = tones(theme);
  const warning = theme.palette.warning.main;
  return {
    position: 'relative',
    overflow: 'hidden',
    borderRadius: '14px',
    border: `1px solid ${alpha(warning, isDark ? 0.42 : 0.3)}`,
    bgcolor: alpha(paper, isDark ? 0.72 : 0.94),
    backgroundImage: `linear-gradient(180deg, ${alpha(warning, isDark ? 0.14 : 0.09)} 0%, transparent 55%)`,
    boxShadow: resolved ? 'none' : `0 12px 28px -18px ${alpha(warning, 0.8)}`,
    opacity: resolved ? 0.72 : 1,
    transition: theme.transitions.create(['opacity', 'box-shadow'], {
      duration: theme.transitions.duration.short,
    }),
    '&::before': {
      content: '""',
      position: 'absolute',
      insetBlock: 0,
      insetInlineStart: 0,
      width: '3px',
      background: `linear-gradient(180deg, ${warning} 0%, ${alpha(warning, 0.25)} 100%)`,
    },
  };
}

/** שורת פרט בכרטיס האישור */
export function assistantConfirmationRowSx(theme: Theme): SystemStyleObject<Theme> {
  return {
    py: 0.375,
    borderTop: `1px solid ${alpha(theme.palette.divider, theme.palette.mode === 'dark' ? 0.4 : 0.7)}`,
    '&:first-of-type': { borderTop: 0 },
  };
}
